import {FC, useEffect, useState} from 'react';
import {useCanAll} from 'src/permissions/hooks';
import {Box, Typography, useTheme} from '@mui/material';
import {QueryDefinition} from '@reduxjs/toolkit/dist/query';
import {LazyQueryTrigger} from '@reduxjs/toolkit/dist/query/react/buildHooks';
import {useIntl} from 'react-intl';
import {BadgeOutlined} from '@mui/icons-material';
import {usersApi} from 'src/api';
import {useBusinessId, useAppSelector} from 'src/app/hooks';
import {AxiosBaseQueryFn} from 'src/axiosBaseQuery';
import {ListParamsType, PageBaseUsersInfo, UsersSearchRequest} from 'src/types';
import {isErrorObject} from 'src/utils/helper';
import {NotifyError} from 'src/utils/notification';
import {ReManagersTable} from 'src/components/common/ReOwner/ReManagersTable';
import {AddReManager} from '../../components/forms/AddReManager';
import {useParams} from 'react-router-dom';
import {filterWrapperSx} from './ReManager.sx';
import {DataFetchProps} from './ReManager.types';
import {UserTypeEnum} from 'src/enums/common';

const handleGetData = (
    usersTrigger: LazyQueryTrigger<
        QueryDefinition<
            UsersSearchRequest,
            AxiosBaseQueryFn,
            'User' | 'Team',
            PageBaseUsersInfo,
            'usersApi'
        >
    >,
    page: number,
    size: number,
    businessId: number,
    realEstateId: number,
    triggerLoadingFlag: () => void
) => {
    return async () => {
        triggerLoadingFlag();
        try {
            const usersResponse = await usersTrigger({
                data: {
                    businessId,
                    realEstateId,
                    userTypes: [UserTypeEnum.REAL_ESTATE],
                },
                params: {
                    page,
                    size,
                },
            }).unwrap();

            return usersResponse;
        } catch (error) {
            if (isErrorObject(error)) {
                NotifyError(error.data.message);
            }
        } finally {
            triggerLoadingFlag();
        }
    };
};

export const ReManager: FC = () => {
    const theme = useTheme();

    const intl = useIntl();
    const {id: reOwnerId} = useParams();



    const [usersTrigger, usersResponse] = usersApi.useLazyGetUsersQuery();

    const {authInfo} = useAppSelector((state) => state.common);
    const businessId = useBusinessId();
    const canCreateManager = useCanAll('users.create', 'accessControl.manageBindings');
    const [tableParams, setTableParams] = useState<ListParamsType>({
        page: 0,
        size: 100,
    });
    const [business, setBusiness] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const callHandler = (businessId: number) =>
        handleGetData(
            usersTrigger,
            tableParams.page,
            tableParams.size,
            businessId,
            reOwnerId ? parseInt(reOwnerId) : 0,
            triggerLoadingFlag
        )();

    const triggerLoadingFlag = () => setIsLoading((prevState) => !prevState);

    const handleDataFetch = ({business}: DataFetchProps) => {
        setBusiness(business);

        callHandler(business);
    };

    const initialDataFetch = async () => {
        try {
            if (!reOwnerId) {
                return;
            }

            handleDataFetch({
                business: businessId,
                reOwner: parseInt(reOwnerId),
            });
        } catch (error) {
            if (isErrorObject(error)) {
                NotifyError(error.data.message);
            }
        }
    };

    useEffect(() => {
        initialDataFetch();
    }, []);

    useEffect(() => {
        if (business === 0) {
            return;
        }

        callHandler(business);
    }, [tableParams]);

    useEffect(() => {
    }, []);

    return (
        <>
            {/* addAction: the two permissions the endpoint behind that button requires, together.
                It was hidden from the platform administrator alone, who holds both. */}
            <ReManagersTable
                title={intl.formatMessage({id: 'reManager'})}
                icon={<BadgeOutlined/>}
                addAction={canCreateManager ? <AddReManager/> : undefined}
                data={usersResponse?.data?.content}
                total={usersResponse?.data?.page?.totalPages ?? 0}
                isLoading={isLoading}
                tableParams={tableParams}
                setTableParams={setTableParams}
            />
        </>
    );
};
